import { getData } from '../actions/apiCall';

export interface Email {
    _id: string;
    from: string;
    to: string[];
    cc?: string[];
    bcc?: string[];
    subject: string;
    body?: string;
    mailContent?: {
        subject: string;
        body: string;
        attachments?: string[];
    };
    mailStatus?: string;
    status?: string;
    createdAt: string;
    updatedAt?: string;
    isPinned?: boolean;
    tags?: Array<{ type: string; name: string }>;
    attachments?: string[];
}

export interface MailServiceResponse {
    data: Email[];
    pagination?: {
        current: number;
        pageSize: number;
        total: number;
    };
    code?: number;
}

export interface MailQueryParams {
    mailStatus?: string;
    status?: string;
    current?: number;
    pageSize?: number;
    search?: string;
    folder?: string;
    userId?: string;
    [key: string]: any;
}

/**
 * Mail Service - Handles all mail-related API calls
 * Returns thunk functions that can be dispatched with Redux
 */
class MailService {
    private mailServiceUrl?: string;

    constructor() {
        this.mailServiceUrl = process.env.MAIL_SERVICE;
    }

    /**
     * Fetch emails with optional query parameters
     * @param queryParams - Query parameters for filtering emails
     * @param useMailService - Whether to use MAIL_SERVICE URL or default API_URL (defaults to true)
     * @param userId - Optional userId to pass for mail service authentication
     * @returns Thunk function that can be dispatched
     */
    getEmails(
        queryParams: MailQueryParams = {},
        useMailService: boolean = true,
        userId?: string
    ) {
        // Use mail service endpoint if available, otherwise fall back to CRM API
        const url = useMailService && this.mailServiceUrl ? '/mail/inbox' : '/api/mails';
        const baseURL = useMailService && this.mailServiceUrl ? this.mailServiceUrl : undefined;

        // Add userId to query params if using mail service
        if (useMailService && this.mailServiceUrl && userId) {
            queryParams.userId = userId;
        }

        return getData(url, queryParams, { baseURL });
    }

    /**
     * Fetch all emails for counting purposes
     * @param useMailService - Whether to use MAIL_SERVICE URL or default API_URL (defaults to true)
     * @param userId - Optional userId to pass for mail service authentication
     * @returns Thunk function that can be dispatched
     */
    getAllEmails(useMailService: boolean = true, userId?: string) {
        return this.getEmails({}, useMailService, userId);
    }

    /**
     * Fetch emails by folder
     * @param folder - Folder name (inbox, drafts, sent, junk, deleted)
     * @param useMailService - Whether to use MAIL_SERVICE URL or default API_URL (defaults to true)
     * @param userId - Optional userId to pass for mail service authentication
     * @returns Thunk function that can be dispatched
     */
    getEmailsByFolder(
        folder: string,
        useMailService: boolean = true,
        userId?: string
    ) {
        const queryParams: MailQueryParams = {};

        // Add folder parameter for mail service
        if (useMailService && this.mailServiceUrl) {
            queryParams.folder = folder;
        } else {
            // For CRM API, use mailStatus filter
            switch (folder) {
                case 'drafts':
                    queryParams.mailStatus = 'Draft';
                    break;
                case 'sent':
                    queryParams.mailStatus = 'Sent';
                    break;
                case 'junk':
                    queryParams.mailStatus = 'Failed';
                    break;
                case 'deleted':
                    queryParams.status = 'deleted';
                    break;
                case 'inbox':
                default:
                    // Inbox: No specific query params, will filter on frontend
                    break;
            }
        }

        return this.getEmails(queryParams, useMailService, userId);
    }

    /**
     * Get inbox emails (non-draft, non-deleted emails)
     * @param useMailService - Whether to use MAIL_SERVICE URL or default API_URL (defaults to true)
     * @returns Thunk function that can be dispatched
     */
    getInboxEmails(useMailService: boolean = true) {
        return this.getEmailsByFolder('inbox', useMailService);
    }

    /**
     * Get draft emails
     * @param useMailService - Whether to use MAIL_SERVICE URL or default API_URL (defaults to true)
     * @returns Thunk function that can be dispatched
     */
    getDraftEmails(useMailService: boolean = true) {
        return this.getEmailsByFolder('drafts', useMailService);
    }

    /**
     * Get sent emails
     * @param useMailService - Whether to use MAIL_SERVICE URL or default API_URL (defaults to true)
     * @returns Thunk function that can be dispatched
     */
    getSentEmails(useMailService: boolean = true) {
        return this.getEmailsByFolder('sent', useMailService);
    }
}

export default new MailService();
